import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { fileMenuOptions, FileOptionId } from "../../data/file-menu-options";
import { GeneralButtonLabel } from "../../data/texts";
import { Icons } from "../../models/assets-files";
import { FileWithId } from "../../models/uploaded-file";
import { FileService } from "../../services/file.service";
import { ImageService } from "../../services/image.service";
import FileUploader from "./FileUploader";
import Image from "./Image";
import useScreen from "./UseScreen";
import { VoidFunction } from "../../models/common.models";

interface FileUploaderListProps {
  fileUploaderSx?: SxProps;
  hideSubmitButton?: boolean;
  singleFile?: boolean;
  onSubmit?: (filesUploaded: FileWithId[], onSuccess: VoidFunction) => void;
  onFileUploaded?: (files: FileWithId[]) => void;
  onFileRemove?: (index: number) => void;
  isLoading?: boolean;
}

const FileUploaderList = ({
  fileUploaderSx,
  hideSubmitButton,
  onFileUploaded,
  isLoading,
  onSubmit,
  singleFile,
  onFileRemove,
}: FileUploaderListProps) => {
  const theme = useTheme();
  const [filesUploaded, setFilesUploaded] = useState<FileWithId[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<FileWithId | null>(null);

  const { isMobile } = useScreen();

  const onFileUpload = (fileList: FileWithId[]) => {
    if (singleFile) {
      setFilesUploaded([fileList[fileList.length - 1]]);
    } else {
      setFilesUploaded((prevFiles) => [...prevFiles, ...fileList]);
    }
  };

  const onFileMenuItemClick = (cta: string) => {
    switch (cta) {
      case FileOptionId.REMOVE:
        if (onFileRemove && selectedFile) {
          const fileIdx = filesUploaded.findIndex(
            (file) => file.id === selectedFile.id
          );
          onFileRemove(fileIdx);
        }
        removeFile(selectedFile?.id);
        break;

      case FileOptionId.VIEW:
        FileService.openFileOnBrowser(selectedFile);
    }
  };

  const onFileUploadedOnParent = () => {
    setFilesUploaded([]);
  };

  useEffect(() => {
    if (onFileUploaded) {
      onFileUploaded(filesUploaded);
    }
  }, [filesUploaded, onFileUploaded]);

  const removeFile = (id?: string) => {
    if (!id) return;
    const fileIdx = filesUploaded.findIndex((file) => file.id === id);
    if (fileIdx === -1) return;

    const filteredFiles = filesUploaded;
    filteredFiles.splice(fileIdx, 1);
    setFilesUploaded(filteredFiles);
  };

  const handleMenuOpen = (
    event?: React.MouseEvent<HTMLElement>,
    file?: FileWithId
  ) => {
    if (event && file) {
      setAnchorEl(event.currentTarget);
      setSelectedFile(file);
      setTimeout(() => {
        window.addEventListener("click", handleMenuClose);
      }, 0);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
    window.removeEventListener("click", handleMenuClose);
  };

  return (
    <Box sx={{ position: "relative" }}>
      {isLoading && (
        <CircularProgress
          sx={{
            position: "absolute",
            left: isMobile ? "43%" : "47%",
            top: "50%",
          }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          ...(isLoading && theme.customStyles.disabled),
        }}
      >
        <FileUploader
          singleFile={singleFile}
          onFilesSelected={onFileUpload}
          sx={{
            height: "232px",
            borderRadius: "8px",
            marginBottom: filesUploaded.length ? "24px" : "0",
            ...fileUploaderSx,
          }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            flexWrap: isMobile ? "wrap" : "nowrap",
            gap: isMobile ? "15px" : "10px",
          }}
        >
          <Box
            sx={{
              ...theme.customStyles.flexStart,
              flexWrap: "wrap",
              gap: "8px",
              flexGrow: "1",
            }}
          >
            {filesUploaded &&
              filesUploaded.map((file) => (
                <Box
                  key={file.id}
                  sx={{
                    border: "1px #EFEFEF solid",
                    maxHeight: "57px",
                    height: "55px",
                    width: isMobile ? "100%" : "30%",
                    paddingBlock: "8px",
                    paddingInline: "12px",
                  }}
                >
                  <Box
                    sx={{
                      ...theme.customStyles.flexBetween,
                    }}
                  >
                    <Typography sx={{ fontSize: "12px" }}>File</Typography>
                    <Image
                      sx={{ cursor: "pointer" }}
                      src={ImageService.getLocalIconPath(
                        Icons.Folders.GENERAL,
                        Icons.General.ThreeDots
                      )}
                      onClick={(ev: React.MouseEvent<HTMLElement>) =>
                        handleMenuOpen(ev, file)
                      }
                    />
                  </Box>
                  <Typography
                    sx={{
                      color: "#002644",
                      fontWeight: "400",
                      fontSize: "14px",
                      ...theme.customStyles.ellipsisOneLine,
                    }}
                  >
                    {file.content?.name}
                  </Typography>
                </Box>
              ))}
          </Box>
          {filesUploaded.length && !hideSubmitButton ? (
            <Button
              onClick={() => onSubmit?.(filesUploaded, onFileUploadedOnParent)}
              variant="contained"
              sx={{
                minWidth: isMobile ? "100%" : "154px",
                background: theme.palette.text.primary,
                ...theme.customStyles.flexStart,
                gap: "8px",
              }}
            >
              <Image
                src={ImageService.getLocalIconPath(
                  Icons.Folders.GENERAL,
                  Icons.General.UploadArrow
                )}
              />
              {GeneralButtonLabel.SUBMIT}
            </Button>
          ) : (
            <></>
          )}
        </Box>

        <Menu
          elevation={0}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          MenuListProps={{
            sx: {
              border: "0.5px solid #7B97AC",
              padding: "0",
              borderRadius: "6px",
            },
          }}
        >
          {fileMenuOptions.map((option) => (
            <MenuItem
              onClick={() => onFileMenuItemClick(option.cta)}
              key={option.cta}
              sx={{
                ...theme.customStyles.flexStart,
                gap: "7px",
              }}
            >
              <Image
                src={ImageService.getLocalIconPath(
                  Icons.Folders.FILE_OPTIONS,
                  option.icon
                )}
              />
              <Typography
                sx={{ color: "#01346BCC", fontSize: "14px", fontWeight: "300" }}
              >
                {option.title}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

export default FileUploaderList;
